import * as CANNON from "cannon-es";
import { gsap } from "gsap";
import * as THREE from "three";
import { BREAK_SEQUENCE, FLOOR_CONFIG } from "./config";

interface FloorTile {
  index: number;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
  strength: number;
  isBreakable: boolean;
  fragment: THREE.Mesh | null;
  body: CANNON.Body | null;
}

interface HudElements {
  fps: HTMLElement | null;
  frame: HTMLElement | null;
  physics: HTMLElement | null;
  bodies: HTMLElement | null;
  fragments: HTMLElement | null;
  drawCalls: HTMLElement | null;
  triangles: HTMLElement | null;
}

export class FloorCollapseSimulation {
  private readonly canvas: HTMLCanvasElement;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
  private readonly baseCameraPosition = new THREE.Vector3();
  private readonly renderer: THREE.WebGLRenderer;
  private readonly world: CANNON.World;
  private readonly defaultMaterial = new CANNON.Material("default");
  private readonly tileMaterial = new THREE.MeshStandardMaterial({
    color: 0xb6bac3,
    roughness: 0.82,
    metalness: 0.04,
  });
  private readonly redMaterial = new THREE.MeshStandardMaterial({
    color: 0xc51622,
    roughness: 0.5,
    metalness: 0.1,
  });
  private readonly tileGeometry = new THREE.BoxGeometry(
    FLOOR_CONFIG.tileSize,
    FLOOR_CONFIG.tileHeight,
    FLOOR_CONFIG.tileSize
  );
  private readonly tileShape = new CANNON.Box(
    new CANNON.Vec3(
      FLOOR_CONFIG.tileSize / 2,
      FLOOR_CONFIG.tileHeight / 2,
      FLOOR_CONFIG.tileSize / 2
    )
  );
  private readonly tiles: FloorTile[] = [];
  private readonly activeFragments: FloorTile[] = [];
  private readonly matrix = new THREE.Matrix4();
  private readonly hiddenMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
  private readonly floor: THREE.InstancedMesh;
  private readonly redCube: THREE.Mesh;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly hud: HudElements;
  private readonly visibilityObserver: IntersectionObserver;

  private animationFrame = 0;
  private resizeTimer = 0;
  private pointerDownX = 0;
  private pointerDownY = 0;
  private isCollapsed = false;
  private isTransitioning = false;
  private physicsActive = false;
  private canvasVisible = true;
  private cameraShakePower = 0;
  private cameraShakeTime = 0;
  private lastTime: number | null = null;
  private fpsFrames = 0;
  private fpsTime = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.scene.background = new THREE.Color(0xd8d8d8);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, FLOOR_CONFIG.maxPixelRatio));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.style.touchAction = "pan-y";

    this.world = this.createPhysicsWorld();
    this.setupLighting();
    this.setupGroundColliders();

    this.floor = this.createFloor();
    this.redCube = this.createTriggerCube();
    this.hud = this.readHudElements();

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.canvasVisible = entry?.isIntersecting ?? false;
      },
      { threshold: 0.08 }
    );
    this.visibilityObserver.observe(this.canvas);

    this.bindEvents();
    this.resize();
    this.animationFrame = requestAnimationFrame(this.animate);
  }

  dispose(): void {
    cancelAnimationFrame(this.animationFrame);
    window.clearTimeout(this.resizeTimer);
    this.visibilityObserver.disconnect();

    for (const tile of this.tiles) {
      if (tile.body) this.world.removeBody(tile.body);
      tile.fragment?.removeFromParent();
    }

    this.floor.removeFromParent();
    this.redCube.removeFromParent();
    this.redCube.geometry.dispose();
    this.tileGeometry.dispose();
    this.tileMaterial.dispose();
    this.redMaterial.dispose();
    this.renderer.dispose();
  }

  private createPhysicsWorld(): CANNON.World {
    const world = new CANNON.World({ gravity: new CANNON.Vec3(0, -9.82, 0) });
    world.broadphase = new CANNON.SAPBroadphase(world);
    world.allowSleep = true;
    world.defaultContactMaterial = new CANNON.ContactMaterial(
      this.defaultMaterial,
      this.defaultMaterial,
      { friction: 0.78, restitution: 0.07 }
    );
    return world;
  }

  private setupLighting(): void {
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    const sun = new THREE.DirectionalLight(0xffffff, 2.4);
    sun.position.set(5, 9, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1536, 1536);
    this.scene.add(sun);
  }

  private setupGroundColliders(): void {
    const lowerGround = new CANNON.Body({
      mass: 0,
      type: CANNON.Body.STATIC,
      material: this.defaultMaterial,
      shape: new CANNON.Plane(),
    });
    lowerGround.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    lowerGround.position.y = FLOOR_CONFIG.lowerGroundY;
    this.world.addBody(lowerGround);

    const floorStep = FLOOR_CONFIG.tileSize + FLOOR_CONFIG.gap;
    const floorSize = FLOOR_CONFIG.gridSize * floorStep;
    const holeSize = FLOOR_CONFIG.explosionRadius * 2;
    const outerBandSize = (floorSize - holeSize) / 2;
    const outerBandCenter = holeSize / 2 + outerBandSize / 2;

    this.addStaticFloorCollider(0, -outerBandCenter, floorSize, outerBandSize);
    this.addStaticFloorCollider(0, outerBandCenter, floorSize, outerBandSize);
    this.addStaticFloorCollider(-outerBandCenter, 0, outerBandSize, holeSize);
    this.addStaticFloorCollider(outerBandCenter, 0, outerBandSize, holeSize);
  }

  private addStaticFloorCollider(x: number, z: number, width: number, depth: number): void {
    const colliderHeight = 0.12;
    const body = new CANNON.Body({
      mass: 0,
      type: CANNON.Body.STATIC,
      material: this.defaultMaterial,
      shape: new CANNON.Box(new CANNON.Vec3(width / 2, colliderHeight / 2, depth / 2)),
      position: new CANNON.Vec3(x, -0.02, z),
    });
    this.world.addBody(body);
  }

  private createFloor(): THREE.InstancedMesh {
    const totalTiles = FLOOR_CONFIG.gridSize * FLOOR_CONFIG.gridSize;
    const floor = new THREE.InstancedMesh(this.tileGeometry, this.tileMaterial, totalTiles);
    floor.castShadow = true;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const floorStep = FLOOR_CONFIG.tileSize + FLOOR_CONFIG.gap;
    let instanceIndex = 0;

    for (let x = -FLOOR_CONFIG.gridSize / 2; x < FLOOR_CONFIG.gridSize / 2; x += 1) {
      for (let z = -FLOOR_CONFIG.gridSize / 2; z < FLOOR_CONFIG.gridSize / 2; z += 1) {
        const px = x * floorStep;
        const pz = z * floorStep;
        const distance = Math.hypot(px, pz);
        const strength = Math.max(0, 1 - distance / FLOOR_CONFIG.explosionRadius);
        const position = new THREE.Vector3(px, 0, pz);
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3(1, 1, 1);

        this.matrix.compose(position, quaternion, scale);
        floor.setMatrixAt(instanceIndex, this.matrix);

        const tile: FloorTile = {
          index: instanceIndex,
          position,
          quaternion,
          scale,
          strength,
          isBreakable: strength > 0,
          fragment: null,
          body: null,
        };

        if (tile.isBreakable) {
          const fragment = new THREE.Mesh(this.tileGeometry, this.tileMaterial);
          fragment.position.copy(position);
          fragment.quaternion.copy(quaternion);
          fragment.castShadow = true;
          fragment.receiveShadow = true;
          fragment.visible = false;
          this.scene.add(fragment);
          tile.fragment = fragment;
        }

        this.tiles.push(tile);
        instanceIndex += 1;
      }
    }

    floor.instanceMatrix.needsUpdate = true;
    return floor;
  }

  private createTriggerCube(): THREE.Mesh {
    const cube = new THREE.Mesh(new THREE.BoxGeometry(0.35, 1.1, 0.35), this.redMaterial);
    cube.position.set(0, 0.58, 0);
    cube.castShadow = true;
    this.scene.add(cube);
    return cube;
  }

  private readHudElements(): HudElements {
    return {
      fps: document.querySelector<HTMLElement>("#hudFps"),
      frame: document.querySelector<HTMLElement>("#hudFrame"),
      physics: document.querySelector<HTMLElement>("#hudPhysics"),
      bodies: document.querySelector<HTMLElement>("#hudBodies"),
      fragments: document.querySelector<HTMLElement>("#hudFragments"),
      drawCalls: document.querySelector<HTMLElement>("#hudDrawCalls"),
      triangles: document.querySelector<HTMLElement>("#hudTriangles"),
    };
  }

  private bindEvents(): void {
    window.addEventListener("resize", this.scheduleResize, { passive: true });

    this.canvas.addEventListener(
      "pointerdown",
      (event) => {
        this.pointerDownX = event.clientX;
        this.pointerDownY = event.clientY;
      },
      { passive: true }
    );

    this.canvas.addEventListener(
      "pointerup",
      (event) => {
        const dx = Math.abs(event.clientX - this.pointerDownX);
        const dy = Math.abs(event.clientY - this.pointerDownY);
        if (dx <= 8 && dy <= 8) this.handlePointer(event);
      },
      { passive: true }
    );
  }

  private readonly scheduleResize = (): void => {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.resize(), 180);
  };

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    this.camera.aspect = rect.width / rect.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(rect.width, rect.height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, FLOOR_CONFIG.maxPixelRatio));
    this.updateCameraPosition();
  }

  private updateCameraPosition(): void {
    if (window.innerWidth < 768) {
      this.baseCameraPosition.set(15, 7.5, 18);
    } else if (window.innerWidth < 1200) {
      this.baseCameraPosition.set(12, 7.5, 14.5);
    } else {
      this.baseCameraPosition.set(9.5, 6.5, 11.5);
    }

    this.camera.position.copy(this.baseCameraPosition);
  }

  private createBody(tile: FloorTile): void {
    if (tile.body) return;

    const body = new CANNON.Body({
      mass: 1.15,
      type: CANNON.Body.DYNAMIC,
      shape: this.tileShape,
      material: this.defaultMaterial,
      position: new CANNON.Vec3(tile.position.x, tile.position.y, tile.position.z),
    });

    body.quaternion.set(tile.quaternion.x, tile.quaternion.y, tile.quaternion.z, tile.quaternion.w);
    body.linearDamping = 0.18;
    body.angularDamping = 0.24;
    body.allowSleep = true;
    body.sleepSpeedLimit = 0.12;
    body.sleepTimeLimit = 0.8;

    this.world.addBody(body);
    tile.body = body;
  }

  private removeBody(tile: FloorTile): void {
    if (!tile.body) return;
    this.world.removeBody(tile.body);
    tile.body = null;
  }

  private hideFloorInstance(tile: FloorTile): void {
    this.floor.setMatrixAt(tile.index, this.hiddenMatrix);
  }

  private showFloorInstance(tile: FloorTile): void {
    this.matrix.compose(tile.position, tile.quaternion, tile.scale);
    this.floor.setMatrixAt(tile.index, this.matrix);
    this.floor.instanceMatrix.needsUpdate = true;
  }

  private collapseFloor(): void {
    if (this.isCollapsed || this.isTransitioning) return;

    this.isCollapsed = true;
    this.isTransitioning = true;
    this.physicsActive = true;
    this.activeFragments.length = 0;

    for (const tile of this.tiles) {
      if (!tile.isBreakable || !tile.fragment) continue;

      const { fragment, strength, position } = tile;
      this.hideFloorInstance(tile);
      fragment.visible = true;
      fragment.position.copy(position);
      fragment.quaternion.copy(tile.quaternion);

      this.createBody(tile);
      tile.body?.wakeUp();
      this.activeFragments.push(tile);

      const delay = strength * 180;

      window.setTimeout(() => {
        tile.body?.velocity.set(0, -3.8 * strength, 0);
        tile.body?.angularVelocity.set(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2
        );
      }, delay);

      window.setTimeout(
        () => this.applyExplosionImpulse(tile),
        BREAK_SEQUENCE.explosionDelayMs + delay
      );
    }

    this.floor.instanceMatrix.needsUpdate = true;
    window.setTimeout(() => {
      this.isTransitioning = false;
    }, BREAK_SEQUENCE.transitionDurationMs);
    window.setTimeout(() => this.freezePhysics(), BREAK_SEQUENCE.freezePhysicsAfterMs);
  }

  private applyExplosionImpulse(tile: FloorTile): void {
    if (!tile.body) return;

    this.cameraShakePower = 0.48;
    this.cameraShakeTime = 1;

    const direction = new CANNON.Vec3(tile.position.x, 0, tile.position.z);
    if (direction.length() === 0) {
      direction.set(Math.random() - 0.5 || 0.01, 0, Math.random() - 0.5 || 0.01);
    }
    direction.normalize();

    const upwardForce = 7 + tile.strength * 12;
    const outwardForce = 5 + tile.strength * 13;

    tile.body.applyImpulse(
      new CANNON.Vec3(
        direction.x * outwardForce + (Math.random() - 0.5) * 4,
        upwardForce,
        direction.z * outwardForce + (Math.random() - 0.5) * 4
      ),
      new CANNON.Vec3(
        tile.body.position.x + (Math.random() - 0.15),
        tile.body.position.y,
        tile.body.position.z + (Math.random() - 0.15)
      )
    );

    tile.body.angularVelocity.set(
      (Math.random() - 0.5) * 16,
      (Math.random() - 0.5) * 16,
      (Math.random() - 0.5) * 16
    );
  }

  private freezePhysics(): void {
    this.physicsActive = false;
    this.activeFragments.forEach((tile) => tile.body?.sleep());
  }

  private restoreFloor(): void {
    if (!this.isCollapsed || this.isTransitioning) return;

    this.isTransitioning = true;
    this.physicsActive = false;

    for (const tile of this.activeFragments) {
      if (!tile.fragment) continue;

      const { fragment, body, position, quaternion } = tile;
      if (body) {
        body.velocity.set(0, 0, 0);
        body.angularVelocity.set(0, 0, 0);
        body.force.set(0, 0, 0);
        body.torque.set(0, 0, 0);
        body.sleep();
      }

      const fromPosition = fragment.position.clone();
      const fromQuaternion = fragment.quaternion.clone();
      const progress = { value: 0 };

      gsap.to(progress, {
        value: 1,
        duration: BREAK_SEQUENCE.restoreDurationSeconds,
        ease: "power3.inOut",
        onUpdate: () => {
          fragment.position.lerpVectors(fromPosition, position, progress.value);
          fragment.quaternion.slerpQuaternions(fromQuaternion, quaternion, progress.value);
        },
        onComplete: () => {
          fragment.position.copy(position);
          fragment.quaternion.copy(quaternion);
          fragment.visible = false;
          this.removeBody(tile);
          this.showFloorInstance(tile);
        },
      });
    }

    window.setTimeout(() => {
      this.activeFragments.length = 0;
      this.isCollapsed = false;
      this.isTransitioning = false;
    }, BREAK_SEQUENCE.restoreCompletionMs);
  }

  private handlePointer(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    if (this.raycaster.intersectObject(this.redCube).length === 0) return;
    if (this.isCollapsed) this.restoreFloor();
    else this.collapseFloor();
  }

  private syncFragments(): void {
    for (const tile of this.activeFragments) {
      if (!tile.body || !tile.fragment) continue;
      tile.fragment.position.set(tile.body.position.x, tile.body.position.y, tile.body.position.z);
      tile.fragment.quaternion.set(
        tile.body.quaternion.x,
        tile.body.quaternion.y,
        tile.body.quaternion.z,
        tile.body.quaternion.w
      );
    }
  }

  private updateHud(delta: number): void {
    if (!this.hud.fps) return;

    this.fpsFrames += 1;
    this.fpsTime += delta;
    if (this.fpsTime < 0.25) return;

    this.hud.fps.textContent = String(Math.round(this.fpsFrames / this.fpsTime));
    if (this.hud.frame) this.hud.frame.textContent = (delta * 1000).toFixed(1);
    if (this.hud.physics) this.hud.physics.textContent = this.physicsActive ? "ON" : "OFF";
    if (this.hud.bodies) this.hud.bodies.textContent = String(this.world.bodies.length);
    if (this.hud.fragments) this.hud.fragments.textContent = String(this.activeFragments.length);
    if (this.hud.drawCalls)
      this.hud.drawCalls.textContent = String(this.renderer.info.render.calls);
    if (this.hud.triangles) {
      this.hud.triangles.textContent = this.renderer.info.render.triangles.toLocaleString();
    }

    this.fpsFrames = 0;
    this.fpsTime = 0;
  }

  private readonly animate = (time: number): void => {
    this.animationFrame = requestAnimationFrame(this.animate);
    if (!this.canvasVisible || document.hidden) {
      this.lastTime = time;
      return;
    }

    const delta = this.lastTime ? Math.min((time - this.lastTime) / 1000, 0.033) : 1 / 60;
    this.lastTime = time;
    this.updateHud(delta);

    if (this.physicsActive) {
      this.world.step(1 / 60, delta, 2);
      this.syncFragments();
    }

    if (this.cameraShakeTime > 0) {
      this.cameraShakeTime -= delta;
      const shake = this.cameraShakePower * this.cameraShakeTime;
      this.camera.position.set(
        this.baseCameraPosition.x + (Math.random() - 0.5) * shake,
        this.baseCameraPosition.y + (Math.random() - 0.5) * shake,
        this.baseCameraPosition.z + (Math.random() - 0.5) * shake
      );
    } else {
      this.camera.position.lerp(this.baseCameraPosition, 0.08);
    }

    this.camera.lookAt(0, window.innerWidth < 768 ? 3 : 0, 0);
    this.renderer.render(this.scene, this.camera);
  };
}
